import { useEffect, useState } from "react";
import { Loader2, Users } from "lucide-react";

const cls = "bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm w-full";

export function SegmentBuilder({ api, societeId, segment, onChange }) {
  const [opts, setOpts] = useState({ tags: [], programs: [], enrollment_statuses: [] });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const seg = segment || {};
  const set = (k, v) => onChange({ ...seg, [k]: v });
  const toggle = (k, v) => set(k, (seg[k] || []).includes(v) ? seg[k].filter((x) => x !== v) : [...(seg[k] || []), v]);

  useEffect(() => { api.get("/marketing/segments/options", { params: { societe_id: societeId } }).then((r) => setOpts(r.data)).catch(() => {}); }, [api, societeId]);
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.post("/marketing/segments/preview", { societe_id: societeId, segment: seg }).then((r) => setPreview(r.data)).catch(() => setPreview(null)).finally(() => setLoading(false));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(seg), societeId]);

  const Chip = ({ active, onClick, children, testId }) => (
    <button type="button" onClick={onClick} data-testid={testId} className={`px-2 py-1 rounded-full text-[11px] border transition-colors ${active ? "bg-[#f7c935] text-[#0b0811] border-[#f7c935] font-bold" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>{children}</button>
  );

  return (
    <div className="space-y-3 rounded-lg border border-white/10 p-3 bg-white/[0.02]" data-testid="segment-builder">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Destinataires ciblés</span>
        <span className="text-xs text-[#f7c935] font-bold" data-testid="segment-count">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : preview ? `${preview.count} destinataire(s)` : "—"}
        </span>
      </div>
      {opts.tags.length > 0 && (
        <div><div className="text-[11px] text-slate-500 mb-1">Tags clients</div>
          <div className="flex flex-wrap gap-1">{opts.tags.map((t) => <Chip key={t} active={(seg.tags || []).includes(t)} onClick={() => toggle("tags", t)} testId={`segment-tag-${t}`}>{t}</Chip>)}</div></div>
      )}
      <div><div className="text-[11px] text-slate-500 mb-1">Programmes de formation (inscrits)</div>
        <div className="flex flex-wrap gap-1">{opts.programs.map((p) => <Chip key={p.program_id} active={(seg.program_ids || []).includes(p.program_id)} onClick={() => toggle("program_ids", p.program_id)} testId={`segment-program-${p.program_id}`}>{p.title}</Chip>)}
          {opts.programs.length === 0 && <span className="text-[11px] text-slate-600">Aucun programme</span>}</div></div>
      {opts.enrollment_statuses.length > 0 && (
        <div><div className="text-[11px] text-slate-500 mb-1">Statut d'inscription</div>
          <div className="flex flex-wrap gap-1">{opts.enrollment_statuses.map((st) => <Chip key={st} active={(seg.enrollment_status || []).includes(st)} onClick={() => toggle("enrollment_status", st)} testId={`segment-status-${st}`}>{st}</Chip>)}</div></div>
      )}
      <div className="grid grid-cols-2 gap-2 items-end">
        <div><div className="text-[11px] text-slate-500 mb-1">Actifs depuis (jours, 0 = tous)</div>
          <input type="number" min="0" value={seg.last_activity_days ?? 0} onChange={(e) => set("last_activity_days", parseInt(e.target.value || "0", 10))} className={cls} data-testid="segment-activity-days" /></div>
        <label className="flex items-center gap-2 text-[11px] text-slate-300 pb-2">
          <input type="checkbox" checked={!!seg.include_students} onChange={(e) => set("include_students", e.target.checked)} data-testid="segment-include-students" /> Inclure les stagiaires sans compte
        </label>
      </div>
      {preview?.sample?.length > 0 && <div className="text-[10px] text-slate-500 truncate" data-testid="segment-sample">Ex. : {preview.sample.slice(0, 4).join(", ")}{preview.unsubscribed ? ` · ${preview.unsubscribed} désabonné(s) exclus` : ""}</div>}
    </div>
  );
}
